import TagManager from 'react-gtm-module';
import { getValue } from '@helpers/localStorage';
import { refCode } from '@helpers/constants/localStorage';
import debounce from 'lodash/debounce';
import { shopifyFormatter } from '@helpers/formatter';

window.dataLayer = window.dataLayer || [];

const gtmIdLocaleMapper = {
  ja: 'GTM-PMF4F83',
  ko: 'GTM-W4FDMWN',
};
const T_KEY_LOCALE = 'trust_arc_locale';
function gtmCode(t) {
  const locale = t(T_KEY_LOCALE);
  if (!!gtmIdLocaleMapper[locale]) {
    const gtmId = gtmIdLocaleMapper[locale];
    const tagManagerArgs = {
      gtmId,
    };
    TagManager.initialize(tagManagerArgs);
  }
}
export { gtmCode };

const GTM_EVENT_PAYLOAD_MAPPER = {
  view_item_list: { item_list_id: '', item_list_name: '', items: [] },
  select_item: { item_list_id: '', item_list_name: '', items: [] },
  view_item: { currency: '', value: '', items: [] },
  add_to_cart: { currency: '', value: '', items: [] },
  view_cart: { currency: '', value: '', items: [] },
  remove_from_cart: { currency: '', value: '', items: [] },
  begin_checkout: { currency: '', value: '', coupon: '', items: [] },
  purchase: {
    currency: '',
    value: '',
    total_value: '',
    shipping: 0,
    tax: 0,
    transaction_id: '',
    coupon: '',
    items: [],
  },
};

const DEFAULT_QUANTITY = 1;

const makeTotalValue = (items) => {
  return items.reduce((total, item) => {
    const { price, quantity = DEFAULT_QUANTITY } = item;

    const itemTotalValue = price * quantity;
    return total + itemTotalValue;
  }, 0);
};

const makeItemParameters = (items, metadata = {}) => {
  const {
    brand = '',
    index: indexFromMetadata,
    item_list_name = '',
    context,
    eventType,
  } = metadata;

  return items.map((item, indexFromList) => {
    const {
      handle = '',
      id = '',
      productName = '',
      shopifyProductId = '',
      title = '',
      index: indexFromItem,
      vendor,
      variants = [],
      quantityRecommended,
      fullPrice = '',
      discountedPrice = '',
      referral: referralId,
      changedQuantity,
      delta,
      discount = 0,
    } = item;

    const calcUnitPrice = (referralId, item) => {
      if (referralId) {
        const [first = {}] = variants;
        const { price: itemPrice = '' } = first;
        const rawPrice = Number(discountedPrice || fullPrice || itemPrice);
        return Number(rawPrice);
      }

      const currencySymbol = context?.storeConfig?.currencySymbol;
      const language = context?.storeConfig?.language;
      const formattedProduct = shopifyFormatter.product(item);

      const hasDiscount = formattedProduct.hasDiscount();
      const fullProductPrice = formattedProduct.fullPrice(
        currencySymbol,
        language
      );
      const discountedProductPrice = formattedProduct.discountedPrice(
        currencySymbol,
        language
      );
      const formattedPrice = hasDiscount
        ? discountedProductPrice
        : fullProductPrice;

      const rawPrice =
        typeof formattedPrice === 'string' &&
        formattedPrice.replace(/[^0-9.]/g, '');
      return Number(rawPrice);
    };

    const price = calcUnitPrice(referralId, item);

    const normId = handle ? id : shopifyProductId;
    const normName = title || handle || productName;

    // for add/remove events, delta or difference in count of item before and after event is used
    // for view events, count of item aka changed or recommended quantity is used

    const quantity =
      (eventType === 'view_cart' && (changedQuantity || quantityRecommended)) ||
      delta ||
      quantityRecommended ||
      DEFAULT_QUANTITY;
    const itemCategory = item_list_name ? { item_list_name } : {};

    return {
      item_id: normId,
      item_name: normName,
      index: indexFromItem || indexFromMetadata || indexFromList,
      item_brand: vendor || brand,
      price, // unit price
      discount,
      quantity,
      ...itemCategory,
    };
  });
};

const handleGtmEventType = (eventType = '', data = {}) => {
  const {
    context,
    referral = {},
    products: itemsFromProducts = [],
    eventParameters,
    metadata,
  } = data;
  const { items: itemsFromReferral = [], id: transaction_id } = referral;

  const items =
    (itemsFromProducts.length && itemsFromProducts) ||
    (itemsFromReferral.length && itemsFromReferral) ||
    [];

  const { item_list_id = '', item_list_name = '' } = eventParameters || {};
  const { storeConfig = {} } = context || {};
  const { currencyCode: currency } = storeConfig;

  const coupon = getValue(refCode);
  const normItems = makeItemParameters(items, {
    ...metadata,
    item_list_name,
    context,
    eventType,
  });
  const value = makeTotalValue(normItems);

  const payload = {
    item_list_id,
    item_list_name,
    items: normItems,
    currency,
    value,
    total_value: value,
    coupon, // refCode
    transaction_id, // referralId
    tax: 0,
    shipping: 0,
  };

  const eventSchema = GTM_EVENT_PAYLOAD_MAPPER[eventType];
  const dataLayerPayload = {};
  for (const key in eventSchema) {
    dataLayerPayload[key] = payload[key];
  }

  const { omnibus, ...omnibusParameters } = eventParameters;
  const pushObject = {
    event: eventType,
    ...(omnibus ? omnibusParameters : { ecommerce: dataLayerPayload }),
  };
  return pushObject;
};

export function handleGtmEvent(
  event,
  { context, referral, products, eventParameters, metadata }
) {
  try {
    let pushObject;
    if (typeof event === 'string') {
      pushObject = handleGtmEventType(event, {
        context,
        referral,
        products,
        eventParameters,
        metadata,
      });
    }

    if (!pushObject) {
      throw new Error('No object to push to dataLayer');
    }

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(pushObject);
  } catch (error) {
    console.error('Error encountered handling gtm event: ', error);
  }
}

export const DEBOUNCE_DELAY_VALUE_MS = 3000;
export const DEBOUNCE_CART_DELAY_VALUE_MS = 5000;
export const DELAY_MULTIPLIER = 1.5;

export const handleGtmEventDebounced = debounce(
  handleGtmEvent,
  DEBOUNCE_DELAY_VALUE_MS
);
